import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// ---------------------------------------------------------------------------
// Standalone-mode portal redirect — synchronous, runs BEFORE React mounts.
//
// When the public portal is added to a phone home-screen, browsers may end
// up launching the app at `/` (the marketing landing → /login) instead of
// `/portal?token=…`. This happens when:
//   • the OLD admin manifest was captured at first install (start_url=`/`)
//   • Safari refuses to honour a dynamic blob: manifest and falls back to
//     the static admin manifest's start_url
//
// We mitigate by remembering the latest portal token in localStorage when
// the user views /portal?token=…, and redirecting on boot if we detect
// "PWA launched standalone AND landed on `/` AND have a saved token".
// Running this here — before any React render — means the user never sees
// the wrong screen flash before the redirect fires.
// ---------------------------------------------------------------------------
(function maybeRedirectStandaloneToPortal() {
  try {
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    if (!isStandalone) return;
    if (window.location.pathname !== "/" && window.location.pathname !== "") return;
    const token = localStorage.getItem("ccs_last_portal_token");
    if (!token) return;
    window.location.replace(`/portal?token=${encodeURIComponent(token)}`);
  } catch (_) {
    // any failure (private mode, storage disabled, etc.) → just let React boot normally
  }
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
